window.BTORoutineImages=window.BTORoutineImages||{};

(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.BTOOilFinderCore = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  const DEFAULT_WEIGHTS = Object.freeze({
    useArea: 25,
    profile: 15,
    goals: 35,
    finish: 15,
    aroma: 5,
    sensitivity: 5
  });

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const finite = (value, fallback = 0) => Number.isFinite(Number(value)) ? Number(value) : fallback;

  function validateProfile(profile) {
    const errors = [];
    if (!profile || typeof profile !== 'object') return ['Profile must be an object.'];
    if (!profile.handle) errors.push('Missing handle.');
    if (!profile.name) errors.push('Missing name.');
    if (!profile.canonicalUrl) errors.push('Missing canonical product reference.');
    if (![1, 2, 3, 4, 5].includes(profile.finish)) errors.push('Finish must be 1–5.');
    if (![1, 2, 3, 4, 5].includes(profile.aroma)) errors.push('Aroma must be 1–5.');
    if (!['yes', 'no', 'unknown'].includes(profile.nutDerivedStatus)) errors.push('Invalid nut-derived status.');
    for (const [groupName, group] of Object.entries({ areas: profile.areas, profiles: profile.profiles, goals: profile.goals })) {
      if (!group || typeof group !== 'object') {
        errors.push(`Missing ${groupName} scores.`);
        continue;
      }
      for (const [key, value] of Object.entries(group)) {
        if (!Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 100) {
          errors.push(`${groupName}.${key} must be 0–100.`);
        }
      }
    }
    return errors;
  }

  function liveProductIsAvailable(liveProduct) {
    return Boolean(liveProduct && Array.isArray(liveProduct.variants) && liveProduct.variants.some((variant) => variant.available));
  }

  function exclusionReasons(profile, answers, liveProduct, options = {}) {
    const reasons = [];
    const validation = validateProfile(profile);
    if (validation.length) reasons.push(`Invalid profile: ${validation.join(' ')}`);
    if (profile.enabled === false) reasons.push('Profile disabled.');
    if (profile.approved !== true) reasons.push('Profile not approved.');
    if (!profile.canonicalUrl) reasons.push('Canonical website product reference missing.');
    if (!liveProductIsAvailable(liveProduct)) reasons.push('No available website variant.');
    if (finite(profile.areas?.[answers.useArea]) < finite(options.minimumUseAreaScore, 50)) reasons.push('Primary use-area score below threshold.');
    if (answers.preferences?.plantOnly && profile.animalDerived === true) reasons.push('Animal-derived product excluded.');
    if (profile.animalDerived === true && answers.preferences?.animalPermission !== true) reasons.push('Animal-derived product lacks explicit permission.');
    if (answers.preferences?.singleIngredientOnly && profile.singleIngredient !== true) reasons.push('Blend excluded by single-ingredient preference.');
    if (answers.preferences?.avoidNut && profile.nutDerivedStatus === 'yes') reasons.push('Nut-derived product excluded.');
    if (answers.preferences?.avoidNut && profile.nutDerivedStatus === 'unknown' && options.excludeUnknownNutStatus !== false) reasons.push('Unknown nut-derived status excluded.');
    return [...new Set(reasons)];
  }

  function weightedGoalScore(profile, rankedGoals) {
    const goals = Array.isArray(rankedGoals) ? rankedGoals.slice(0, 3) : [];
    if (!goals.length) return null;
    const priorities = [20, 10, 5];
    let total = 0;
    let weight = 0;
    goals.forEach((goal, index) => {
      total += finite(profile.goals?.[goal]) * priorities[index];
      weight += priorities[index];
    });
    return weight ? total / weight : null;
  }

  function proximityScore(actual, desired) {
    if (desired === null || desired === undefined || desired === '' || !Number.isFinite(Number(desired))) return null;
    return clamp(100 - Math.abs(finite(actual) - finite(desired)) * 25, 0, 100);
  }

  function tieBreakerAdjustment(profile, tieBreaker) {
    if (!tieBreaker?.kind || !tieBreaker?.value) return 0;
    if (tieBreaker.kind === 'finish') {
      if (tieBreaker.value === 'lighter') return (5 - finite(profile.finish, 3)) * 2;
      if (tieBreaker.value === 'richer') return (finite(profile.finish, 3) - 1) * 2;
    }
    if (tieBreaker.kind === 'aroma') {
      if (tieBreaker.value === 'neutral') return (5 - finite(profile.aroma, 3)) * 2;
      if (tieBreaker.value === 'distinctive') return (finite(profile.aroma, 3) - 1) * 2;
    }
    if (tieBreaker.kind === 'approach') {
      if (tieBreaker.value === 'simple') return finite(profile.beginnerFriendly) >= 80 ? 6 : 0;
      if (tieBreaker.value === 'specialized') return finite(profile.beginnerFriendly) < 80 ? 6 : 0;
    }
    return 0;
  }

  function scoreProfile(profile, answers, weights = DEFAULT_WEIGHTS) {
    const components = {
      useArea: finite(profile.areas?.[answers.useArea]),
      profile: answers.profile ? finite(profile.profiles?.[answers.profile]) : null,
      goals: weightedGoalScore(profile, answers.goals),
      finish: proximityScore(profile.finish, answers.finish),
      aroma: proximityScore(profile.aroma, answers.aroma),
      sensitivity: answers.sensitive ? finite(profile.sensitivity) : null
    };
    let activeWeight = 0;
    let weightedTotal = 0;
    for (const [key, value] of Object.entries(components)) {
      if (value === null || value === undefined) continue;
      const weight = finite(weights[key]);
      activeWeight += weight;
      weightedTotal += value * weight;
    }
    const baseScore = activeWeight ? weightedTotal / activeWeight : 0;
    return {
      score: clamp(baseScore + tieBreakerAdjustment(profile, answers.tieBreaker), 0, 100),
      components,
      activeWeight
    };
  }

  function confidenceBand(score) {
    if (score >= 85) return 'Excellent fit';
    if (score >= 72) return 'Strong fit';
    if (score >= 60) return 'Good starting point';
    return 'Possible starting point';
  }

  function chooseAlternatives(primary, ranked, minimumScore = 58, useArea = null) {
    if (!primary) return [];
    const eligible = ranked.filter((item) => item.profile.handle !== primary.profile.handle && item.score >= minimumScore);
    const choices = [];
    const lighter = eligible.find((item) => item.profile.finish <= primary.profile.finish - 1 || item.profile.aroma <= primary.profile.aroma - 1);
    const richer = eligible.find((item) => item.profile.finish >= primary.profile.finish + 1 || (useArea === 'massage' && finite(item.profile.areas?.massage) >= finite(primary.profile.areas?.massage) + 15));
    if (lighter) choices.push({ ...lighter, distinction: lighter.profile.finish < primary.profile.finish ? 'Choose this instead for a lighter finish.' : 'Choose this instead when a more neutral aroma matters most.' });
    if (richer && !choices.some((item) => item.profile.handle === richer.profile.handle)) choices.push({ ...richer, distinction: richer.profile.finish > primary.profile.finish ? 'Choose this instead for a richer finish.' : useArea === 'massage' ? 'Choose this instead for longer massage glide.' : `Choose this instead for a different ${richer.profile.copy?.texture?.toLowerCase() || 'sensory'} feel.` });
    for (const item of eligible) {
      if (choices.length >= 2) break;
      if (!choices.some((choice) => choice.profile.handle === item.profile.handle)) choices.push({ ...item, distinction: `Choose this instead for a different ${item.profile.copy?.texture?.toLowerCase() || 'sensory'} feel.` });
    }
    return choices.slice(0, 2);
  }

  function rankProducts(answers, profiles, liveProducts, logicVersion, options = {}) {
    const debug = { logicVersion, excluded: [], eligible: [] };
    const ranked = [];
    for (const profile of profiles || []) {
      if (!profile || typeof profile !== 'object') {
        debug.excluded.push({ handle: null, reasons: ['Invalid profile: Profile must be an object.'] });
        continue;
      }
      const liveProduct = liveProducts?.[profile.handle];
      const reasons = exclusionReasons(profile, answers, liveProduct, options);
      if (reasons.length) {
        debug.excluded.push({ handle: profile.handle, reasons });
        continue;
      }
      const scored = scoreProfile(profile, answers, options.weights || DEFAULT_WEIGHTS);
      const item = { profile, liveProduct, ...scored };
      ranked.push(item);
      debug.eligible.push({ handle: profile.handle, score: scored.score, components: scored.components });
    }
    ranked.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      const firstGoal = answers.goals?.[0];
      if (firstGoal && finite(b.profile.goals?.[firstGoal]) !== finite(a.profile.goals?.[firstGoal])) return finite(b.profile.goals?.[firstGoal]) - finite(a.profile.goals?.[firstGoal]);
      if (finite(b.profile.dataCompleteness) !== finite(a.profile.dataCompleteness)) return finite(b.profile.dataCompleteness) - finite(a.profile.dataCompleteness);
      if (finite(b.profile.beginnerFriendly) !== finite(a.profile.beginnerFriendly)) return finite(b.profile.beginnerFriendly) - finite(a.profile.beginnerFriendly);
      if (finite(b.profile.versatility) !== finite(a.profile.versatility)) return finite(b.profile.versatility) - finite(a.profile.versatility);
      return String(a.profile.handle).localeCompare(String(b.profile.handle));
    });
    const primary = ranked[0] || null;
    return {
      primary,
      alternatives: chooseAlternatives(primary, ranked, options.minimumAlternativeScore, answers?.useArea),
      ranked,
      confidence: primary ? confidenceBand(primary.score) : null,
      needsTieBreaker: Boolean(primary && ranked[1] && Math.abs(primary.score - ranked[1].score) <= finite(options.tieThreshold, 5)),
      debug
    };
  }

  function chooseTieBreaker(first, second) {
    if (!first || !second) return null;
    if (Math.abs(finite(first.profile.finish) - finite(second.profile.finish)) >= 1) {
      return { kind: 'finish', question: 'For this close match, which finish sounds better?', options: [{ value: 'lighter', label: 'Lighter and faster-feeling' }, { value: 'richer', label: 'Richer with more cushion' }] };
    }
    if (Math.abs(finite(first.profile.aroma) - finite(second.profile.aroma)) >= 1) {
      return { kind: 'aroma', question: 'One last preference: what about natural aroma?', options: [{ value: 'neutral', label: 'As neutral as possible' }, { value: 'distinctive', label: 'A distinctive botanical aroma is fine' }] };
    }
    return { kind: 'approach', question: 'Which kind of starting point feels more like you?', options: [{ value: 'simple', label: 'The simplest versatile choice' }, { value: 'specialized', label: 'A more specialized oil' }] };
  }

  function selectVariant(liveProduct) {
    const variants = (liveProduct?.variants || []).filter((variant) => variant.available);
    if (!variants.length) return null;
    // Every recommendation opens on 4 oz, regardless of quiz answers or catalog order.
    // If unavailable, prefer the next small available bottle before bulk sizes.
    const targets = ['4 oz', '2 oz', '8 oz', '16 oz', '32 oz', '64 oz', '128 oz', '1 gallon'];
    const normalizedSize = (title) => {
      const value = String(title || '').toLowerCase().trim();
      const ounceMatch = value.match(/(?:^|\s)(\d+(?:\.\d+)?)\s*(?:fl\s*)?oz(?:\s|$)/);
      if (ounceMatch) return `${ounceMatch[1]} oz`;
      if (/\b(?:1\s*)?gallon\b/.test(value)) return '1 gallon';
      return value;
    };
    for (const target of targets) {
      const match = variants.find((variant) => normalizedSize(variant.title) === target);
      if (match) return match;
    }
    return variants[0];
  }

  // Composition facts are ingredient-level descriptions, not batch assay values.
  // Evidence and copy review: qa/quiz-conversion-20260906/SOURCES.md.
  const NUTRIENT_PROFILES = Object.freeze({
    'apricot-kernel-oil': { title: 'Oleic + linoleic fatty acids', text: 'A naturally occurring fatty-acid blend in this versatile conditioning oil.' },
    'golden-jojoba-oil': { title: 'Naturally occurring wax esters', text: 'Jojoba is a liquid wax, giving your routine a distinctive silky, cushioning feel.' },
    'olive-squalane': { title: 'Lightweight skin-conditioning lipid', text: 'Squalane softens the feel of skin with a smooth, nearly weightless finish.' },
    'cucumber-seed-oil': { title: 'Linoleic acid · omega-6', text: 'Cucumber seed oil naturally contains this essential fatty acid in its conditioning lipid profile.' },
    'rosehip-seed-oil': { title: 'Omega-3 + omega-6 fatty acids', text: 'Rosehip seed oil naturally contains alpha-linolenic and linoleic acids.' },
    'sweet-almond-oil': { title: 'Oleic acid + natural vitamin E', text: 'Sweet almond oil naturally contains oleic acid and tocopherols, forms of vitamin E.' },
    'avocado-oil': { title: 'Oleic acid · omega-9', text: 'Oleic acid is a prominent part of avocado oil’s naturally rich lipid profile.' },
    'argan-oil': { title: 'Oleic + linoleic fatty acids', text: 'These naturally occurring lipids are part of argan oil’s botanical conditioning profile.' },
    'amla-oil': { title: 'Botanical conditioning', text: 'A substantial oil texture for a deliberate scalp and pre-wash hair ritual.' },
    'castor-oil': { title: 'Ricinoleic acid', text: 'Castor oil is naturally rich in this distinctive fatty acid and has a thick, substantial feel.' }
  });

  const ROUTINE_PAIRS = [
    ['olive-squalane', 'rosehip-seed-oil'], ['golden-jojoba-oil', 'castor-oil'],
    ['amla-oil', 'argan-oil'], ['sweet-almond-oil', 'avocado-oil'],
    ['apricot-kernel-oil', 'avocado-oil'], ['cucumber-seed-oil', 'golden-jojoba-oil']
  ];

  function nutrientProfile(handle) {
    return NUTRIENT_PROFILES[handle] || null;
  }

  function selectCompanion(result, answers) {
    if (answers.complexity === 'one' || !result.primary) return null;
    const handle = result.primary.profile.handle;
    const eligible = result.ranked.filter(item => item.profile.handle !== handle && item.score >= 60);
    const pair = ROUTINE_PAIRS.find(handles => handles.includes(handle));
    const pairedHandle = pair?.find(candidate => candidate !== handle);
    return eligible.find(item => item.profile.handle === pairedHandle) || eligible[0] || null;
  }

  function pairingDetails(primary, companion, answers) {
    const handles = [primary.profile.handle, companion.profile.handle];
    const has = handle => handles.includes(handle);
    const goal = {
      softness: 'softer-feeling skin or hair', lightweight: 'a light, comfortable finish',
      glow: 'a healthy-looking glow', shine: 'smoother-looking, shinier hair',
      rich: 'richer conditioning', scalp: 'a more comfortable scalp-care ritual',
      glide: 'smooth massage glide', simplicity: 'an easy everyday routine'
    }[answers.goals?.[0]] || 'your everyday care goals';
    const area = answers.useArea;
    let title = 'Give your routine a second strength.';
    let benefit = `${primary.profile.name} brings a ${primary.profile.copy.texture} feel; ${companion.profile.name} adds a ${companion.profile.copy.texture} option. Reach for the texture that suits each part of your routine.`;
    let ritual = `${primary.profile.routines[area]} For your second step: ${companion.profile.routines[area]}`;
    if (has('olive-squalane') && has('rosehip-seed-oil')) {
      title = 'Softness by day. Glow by night.';
      benefit = 'Squalane gives daytime care a light, silky finish. Rosehip adds omega-3 and omega-6 fatty acids to a richer evening ritual for soft, supple-looking skin.';
      ritual = 'Smooth a few drops of Squalane over damp skin in the morning; press two drops of Rosehip onto damp skin at night.';
    } else if (has('golden-jojoba-oil') && has('castor-oil')) {
      title = 'Easy spread. Richer conditioning.';
      benefit = 'Jojoba’s silky wax esters make a small amount easy to distribute. Castor brings a thicker, ricinoleic-acid-rich texture for targeted conditioning.';
      ritual = area === 'scalp' || area === 'hair' ? 'Blend one drop of Castor with two or three drops of Jojoba in your palm, apply sparingly before washing, then shampoo thoroughly.' : 'Blend one drop of Castor with two or three drops of Jojoba in your palm and smooth sparingly over dry-feeling areas.';
    } else if (has('amla-oil') && has('argan-oil')) {
      title = 'A richer ritual. A silkier finish.';
      benefit = 'Amla gives your pre-wash routine substantial conditioning. Argan’s oleic and linoleic fatty acids accompany a lighter finishing step for soft, polished-looking lengths.';
      ritual = 'Use Amla before shampooing. After washing, smooth one or two drops of Argan through the lower lengths and ends.';
    } else if (has('avocado-oil') && (has('sweet-almond-oil') || has('apricot-kernel-oil'))) {
      const partner = has('sweet-almond-oil') ? 'Sweet Almond' : 'Apricot Kernel';
      title = 'Everyday softness. Extra cushion.';
      benefit = `${partner} brings smooth everyday glide. Oleic-acid-rich Avocado adds a more substantial feel when dry areas or coarse-feeling ends need extra conditioning.`;
      ritual = area === 'hair' || area === 'scalp' ? `Use ${partner} sparingly for everyday care; choose Avocado for a richer pre-wash treatment, then shampoo.` : `Start with ${partner} over damp skin. Massage a small amount of Avocado into areas where you want extra cushion.`;
    } else if (has('cucumber-seed-oil') && has('golden-jojoba-oil')) {
      title = 'Light care. Silky comfort.';
      benefit = 'Cucumber Seed contributes linoleic acid, an omega-6 fatty acid. Jojoba adds a silky liquid-wax texture, giving you two useful ways to soften the feel of dry areas.';
      ritual = 'Use a few drops of Cucumber Seed for light daily care; smooth Jojoba onto areas where you want a little more silky cushion.';
    }
    return { title, benefit, ritual, goal: `Chosen around your priority: ${goal}.` };
  }

  return {
    nutrientProfile,
    selectCompanion,
    pairingDetails,
    DEFAULT_WEIGHTS,
    chooseTieBreaker,
    confidenceBand,
    exclusionReasons,
    rankProducts,
    scoreProfile,
    selectVariant,
    validateProfile
  };
});

(function () {
  'use strict';

  const SELECTOR = '[data-bto-routine-builder]';
  const STORAGE_KEY = 'bto-routine-builder:v2';
  const LOGIC_VERSION = 'bto-oil-finder-2026-08-11-v2';
  const GOAL_WEIGHTS = { useArea: 25, profile: 15, goals: 35, finish: 15, aroma: 5, sensitivity: 5 };
  const Core = window.BTOOilFinderCore;
  if (!Core) return;

  const AREA_OPTIONS = [
    ['face', 'Face', 'A few drops for a simple face routine'],
    ['body', 'Body', 'After-shower softness and targeted care'],
    ['hair', 'Hair lengths + ends', 'Polish, softness, and pre-wash care'],
    ['scalp', 'Scalp + roots', 'A deliberate pre-wash massage'],
    ['beard', 'Beard', 'Skin beneath, softness, and grooming'],
    ['massage', 'Massage', 'Personal or professional glide']
  ];

  const PROFILE_OPTIONS = {
    face: [['dry', 'Dry-feeling', 'I want more softness and comfort'], ['combination', 'Combination', 'Some areas want lightness, others more cushion'], ['sensitive', 'Easily bothered', 'I prefer a cautious, simple starting point'], ['first-time', 'New to face oils', 'I want the easiest place to begin']],
    body: [['dry', 'Dry-feeling', 'I want more softness after bathing'], ['sensitive', 'Easily bothered', 'I prefer a cautious, simple starting point'], ['first-time', 'New to body oils', 'I want one straightforward bottle'], ['personal-massage', 'Body + massage', 'I want care with comfortable glide']],
    hair: [['fine-hair', 'Fine or easily weighed down', 'A little oil goes a long way'], ['thick-hair', 'Thick or coarse', 'I want more substantial softness'], ['textured-hair', 'Curly, coily, or textured', 'I want definition-friendly cushion'], ['dry-ends', 'Dry-looking ends', 'My lower lengths are the focus']],
    scalp: [['dry-scalp', 'Dry-feeling scalp', 'Comfort is my first priority'], ['pre-wash', 'Pre-wash ritual', 'I plan to shampoo after'], ['sensitive', 'Easily bothered', 'I prefer a cautious, simple starting point'], ['first-time', 'New to scalp oils', 'I want the easiest place to begin']],
    beard: [['short-beard', 'Short beard', 'Light everyday grooming'], ['coarse-beard', 'Longer or coarse', 'More softness and control'], ['skin-under-beard', 'Skin beneath', 'A comfortable-feeling base matters most'], ['first-time', 'New to beard oil', 'Keep the first routine simple']],
    massage: [['personal-massage', 'At-home massage', 'Comfortable everyday glide'], ['pro-massage', 'Professional use', 'Reliable spread across a session'], ['long-glide', 'Longest glide', 'I want fewer reapplications'], ['light-cleanup', 'Lighter cleanup', 'I prefer less residue afterward']]
  };

  const GOAL_OPTIONS = {
    face: [['softness', 'Soft comfort'], ['lightweight', 'Barely-there finish'], ['glow', 'Healthy-looking glow'], ['simplicity', 'One easy everyday oil']],
    body: [['softness', 'Soft comfort'], ['rich', 'More cushion'], ['glide', 'Easy spread + glide'], ['simplicity', 'One versatile bottle']],
    hair: [['softness', 'Softer-feeling ends'], ['shine', 'Polished-looking shine'], ['lightweight', 'No weighed-down feel'], ['rich', 'Richer pre-wash care']],
    scalp: [['scalp', 'Pre-wash scalp massage'], ['lightweight', 'Lighter application'], ['rich', 'More cushion'], ['simplicity', 'Easy first routine']],
    beard: [['softness', 'Softer beard feel'], ['shine', 'Polished finish'], ['lightweight', 'Non-heavy grooming'], ['simplicity', 'One ready-to-use step']],
    massage: [['glide', 'Comfortable glide'], ['rich', 'More cushion'], ['lightweight', 'Lighter cleanup'], ['simplicity', 'One versatile body oil']]
  };

  const FINISH_OPTIONS = [
    [1, 'Barely there', 'Fast-feeling and featherlight'],
    [2, 'Light + silky', 'A polished everyday feel'],
    [3, 'Balanced', 'Some glide without feeling very rich'],
    [4, 'Rich + cushiony', 'A more substantial ritual'],
    [null, 'No preference', 'Let my other answers decide']
  ];

  const COMPLEXITY_OPTIONS = [
    ['one', 'One essential', 'Give me the single best bottle'],
    ['two', 'Simple two-step', 'A primary oil plus one purposeful companion'],
    ['both', 'Show me both', 'Lead with one bottle and show the optional upgrade']
  ];

  const CONTEXT_OPTIONS = [
    ['first', 'First bottle', 'A small, approachable place to begin'],
    ['regular', 'Regular routine', 'An everyday size for consistent use'],
    ['household', 'Household staple', 'A shared routine with repeat use'],
    ['professional', 'Studio or maker', 'Frequent use for work or projects']
  ];

  const LABELS = {
    areas: Object.fromEntries(AREA_OPTIONS.map(([value, label]) => [value, label])),
    profiles: Object.fromEntries(Object.values(PROFILE_OPTIONS).flat().map(([value, label]) => [value, label])),
    goals: Object.fromEntries(Object.values(GOAL_OPTIONS).flat().map(([value, label]) => [value, label])),
    finish: Object.fromEntries(FINISH_OPTIONS.map(([value, label]) => [String(value), label])),
    complexity: Object.fromEntries(COMPLEXITY_OPTIONS.map(([value, label]) => [value, label])),
    context: Object.fromEntries(CONTEXT_OPTIONS.map(([value, label]) => [value, label]))
  };

  const escapeHTML = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[char]));
  const safeJson = (element) => {
    try { return JSON.parse(element?.textContent || 'null'); } catch (_) { return null; }
  };
  const privacyAllowed = (kind) => {
    const privacy = window.Shopify?.customerPrivacy;
    const method = kind === 'analytics' ? 'analyticsProcessingAllowed' : 'preferencesProcessingAllowed';
    try { return typeof privacy?.[method] === 'function' && privacy[method]() === true; }
    catch (_) { return false; }
  };
  const pushEvent = (event, extra = {}) => {
    if (!privacyAllowed('analytics')) return;
    const eventName = `bella_terra_oil_finder_${event}`;
    const payload = { finder_logic_version: LOGIC_VERSION, ...extra };
    try {
      if (typeof window.Shopify?.analytics?.publish === 'function') {
        window.Shopify.analytics.publish(eventName, payload);
        return;
      }
    } catch (_) {}
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...payload });
  };

  const defaultState = () => ({
    phase: 'intro',
    step: 0,
    answers: {
      useArea: null,
      profile: null,
      goals: [],
      finish: null,
      aroma: null,
      sensitive: false,
      preferences: { plantOnly: true, singleIngredientOnly: false, avoidNut: false },
      complexity: null,
      context: null,
      tieBreaker: null
    },
    restored: false
  });

  const sanitizeAnswers = (raw) => {
    const clean = defaultState().answers;
    if (!raw || typeof raw !== 'object') return clean;
    const area = AREA_OPTIONS.some(([value]) => value === raw.useArea) ? raw.useArea : null;
    clean.useArea = area;
    clean.profile = (PROFILE_OPTIONS[area] || []).some(([value]) => value === raw.profile) ? raw.profile : null;
    const allowedGoals = new Set((GOAL_OPTIONS[area] || []).map(([value]) => value));
    clean.goals = Array.isArray(raw.goals) ? [...new Set(raw.goals.filter((goal) => allowedGoals.has(goal)))].slice(0, 3) : [];
    if (['1', '2', '3', '4', 'none'].includes(String(raw.finishChoice))) {
      clean.finishChoice = String(raw.finishChoice);
      clean.finish = clean.finishChoice === 'none' ? null : Number(clean.finishChoice);
    }
    if (['1', '3', 'none'].includes(String(raw.aromaChoice))) {
      clean.aromaChoice = String(raw.aromaChoice);
      clean.aroma = clean.aromaChoice === 'none' ? null : Number(clean.aromaChoice);
    }
    clean.sensitive = raw.sensitive === true;
    clean.preferences = {
      plantOnly: true,
      singleIngredientOnly: raw.preferences?.singleIngredientOnly === true,
      avoidNut: raw.preferences?.avoidNut === true
    };
    clean.complexity = COMPLEXITY_OPTIONS.some(([value]) => value === raw.complexity) ? raw.complexity : null;
    clean.context = CONTEXT_OPTIONS.some(([value]) => value === raw.context) ? raw.context : null;
    if (raw.tieBreaker && ['finish', 'aroma', 'approach'].includes(raw.tieBreaker.kind) && typeof raw.tieBreaker.value === 'string') {
      clean.tieBreaker = { kind: raw.tieBreaker.kind, value: raw.tieBreaker.value };
    }
    return clean;
  };

  const readState = () => {
    if (!privacyAllowed('preferences')) {
      try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
      return defaultState();
    }
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY));
      if (stored?.logicVersion !== LOGIC_VERSION || !stored.answers || !['intro', 'questions', 'tie', 'results'].includes(stored.phase)) return defaultState();
      const answers = sanitizeAnswers(stored.answers);
      const hasFinish = Object.prototype.hasOwnProperty.call(answers, 'finishChoice');
      const complete = Boolean(answers.useArea && answers.profile && answers.goals.length && hasFinish && answers.complexity && answers.context);
      let maxStep = 0;
      if (answers.useArea) maxStep = 1;
      if (answers.profile) maxStep = 2;
      if (answers.goals.length) maxStep = 3;
      if (hasFinish) maxStep = 5;
      if (answers.complexity) maxStep = 6;
      const requestedStep = Number.isInteger(stored.step) ? stored.step : 0;
      const phase = ['tie', 'results'].includes(stored.phase) && !complete ? 'questions' : stored.phase;
      if (phase === 'intro') return defaultState();
      return { phase, step: Math.max(0, Math.min(6, maxStep, requestedStep)), answers, restored: true };
    } catch (_) { return defaultState(); }
  };

  function init(root) {
    if (root.dataset.btoReady === 'true') return;
    root.dataset.btoReady = 'true';
    document.body.classList.add('bto-routine-template');

    const profiles = safeJson(root.querySelector('[data-routine-profiles]')) || [];
    const liveProducts = safeJson(root.querySelector('[data-routine-products]')) || {};
    const app = root.querySelector('[data-routine-app]');
    const stage = root.querySelector('[data-routine-stage]');
    const liveRegion = root.querySelector('[data-routine-live]');
    const currency = root.dataset.currency || 'USD';
    let state = readState();
    let lastResult = null;
    const imagePromises = new Map();
    let resultImageObserver = null;

    const save = () => {
      if (!privacyAllowed('preferences')) return;
      try { window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ logicVersion: LOGIC_VERSION, phase: state.phase, step: state.step, answers: state.answers })); } catch (_) {}
    };
    const announce = (message) => { if (liveRegion) liveRegion.textContent = message; };
    const formatMoney = (cents) => {
      try { return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(Number(cents || 0) / 100); }
      catch (_) { return `$${(Number(cents || 0) / 100).toFixed(2)}`; }
    };
    const areaOptions = () => AREA_OPTIONS;
    const profileOptions = () => PROFILE_OPTIONS[state.answers.useArea] || [];
    const goalOptions = () => GOAL_OPTIONS[state.answers.useArea] || [];
    const goalLabel = (goal) => goalOptions().find(([value]) => value === goal)?.[1] || LABELS.goals[goal] || goal;
    const steps = [
      { key: 'useArea', eyebrow: 'Your routine', title: 'Where will you use it most?', note: 'Choose the one area you want us to solve first.', options: areaOptions },
      { key: 'profile', eyebrow: 'A little context', title: 'Which sounds most like you?', note: 'This helps us separate a versatile first oil from a more specialized one.', options: profileOptions },
      { key: 'goals', eyebrow: 'Rank what matters', title: 'What matters most today?', note: 'Choose up to three. The first choice counts most, and you can change the order.', options: goalOptions },
      { key: 'finish', eyebrow: 'The sensory match', title: 'How should the oil feel?', note: 'There is no wrong texture—this is about what you will actually enjoy using.', options: () => FINISH_OPTIONS },
      { key: 'preferences', eyebrow: 'Keep it comfortable', title: 'Any preferences we should respect?', note: 'These answers can remove products before scoring. You can leave every optional box unchecked.', options: () => [] },
      { key: 'complexity', eyebrow: 'Build the routine', title: 'How much routine do you want?', note: 'We will never turn a one-bottle answer into a forced bundle.', options: () => COMPLEXITY_OPTIONS },
      { key: 'context', eyebrow: 'Your everyday routine', title: 'How often will this bottle work?', note: 'Your results start with 4 oz when available. You can choose a different size before adding to cart.', options: () => CONTEXT_OPTIONS }
    ];
    const completedSteps = new Set();

    const currentStep = () => steps[state.step];
    const refocus = (selector) => window.requestAnimationFrame(() => stage.querySelector(selector)?.focus({ preventScroll: true }));
    const isComplete = () => {
      const key = currentStep().key;
      if (key === 'goals') return state.answers.goals.length > 0;
      if (key === 'preferences') return true;
      if (key === 'finish') return Object.prototype.hasOwnProperty.call(state.answers, 'finishChoice');
      return Boolean(state.answers[key]);
    };

    const optionMarkup = (step, options) => {
      const name = `bto-${step.key}`;
      return `<fieldset class="bto-routine-options"><legend class="bto-routine-srOnly">${escapeHTML(step.title)}</legend>${options.map(([value, label, detail], index) => {
        const normalized = value === null ? 'none' : String(value);
        const checked = step.key === 'finish'
          ? String(state.answers.finishChoice) === normalized
          : String(state.answers[step.key]) === String(value);
        return `<label class="bto-routine-option${checked ? ' is-selected' : ''}">
          <input type="radio" name="${name}" value="${escapeHTML(normalized)}" data-answer="${step.key}"${checked ? ' checked' : ''}>
          <span class="bto-routine-optionMark" aria-hidden="true">${String(index + 1).padStart(2, '0')}</span>
          <span><strong>${escapeHTML(label)}</strong>${detail ? `<small>${escapeHTML(detail)}</small>` : ''}</span>
          <i aria-hidden="true"></i>
        </label>`;
      }).join('')}</fieldset>`;
    };

    const goalsMarkup = (step) => {
      const selected = state.answers.goals;
      return `<fieldset class="bto-routine-options bto-routine-options--goals"><legend class="bto-routine-srOnly">${escapeHTML(step.title)}</legend>${step.options().map(([value, label]) => {
        const rank = selected.indexOf(value);
        return `<div class="bto-routine-goal${rank >= 0 ? ' is-selected' : ''}">
          <label><input type="checkbox" value="${escapeHTML(value)}" data-goal${rank >= 0 ? ' checked' : ''}><span class="bto-routine-rank">${rank >= 0 ? rank + 1 : '+'}</span><strong>${escapeHTML(label)}</strong></label>
          ${rank >= 0 ? `<span class="bto-routine-rankControls" aria-label="Change ${escapeHTML(label)} priority"><button type="button" data-rank-up="${escapeHTML(value)}" aria-label="Move ${escapeHTML(label)} up"${rank === 0 ? ' disabled' : ''}>↑</button><button type="button" data-rank-down="${escapeHTML(value)}" aria-label="Move ${escapeHTML(label)} down"${rank === selected.length - 1 ? ' disabled' : ''}>↓</button></span>` : ''}
        </div>`;
      }).join('')}</fieldset><p class="bto-routine-selectionNote" aria-live="polite">${selected.length ? `${selected.length} selected · ${selected.map((goal) => escapeHTML(goalLabel(goal))).join(' → ')}` : 'Choose at least one priority.'}</p>`;
    };

    const preferencesMarkup = () => {
      const preferences = state.answers.preferences;
      const checks = [
        ['sensitive', 'My skin or scalp is easily bothered', 'This favors simpler, cautious starting points.'],
        ['singleIngredientOnly', 'Single-ingredient oils only', 'This removes formulated blends.'],
        ['avoidNut', 'Avoid nut or kernel-derived oils', 'We also remove products whose status is not yet verified.']
      ];
      const aromas = [[1, 'Keep the natural aroma very mild'], [3, 'A noticeable botanical aroma is fine'], ['none', 'No aroma preference']];
      return `<fieldset class="bto-routine-preferenceGroup"><legend>Natural aroma</legend><div class="bto-routine-smallChoices">${aromas.map(([value, label]) => `<label class="${String(state.answers.aromaChoice) === String(value) ? 'is-selected' : ''}"><input type="radio" name="bto-aroma" value="${escapeHTML(value)}" data-aroma${String(state.answers.aromaChoice) === String(value) ? ' checked' : ''}><span>${escapeHTML(label)}</span></label>`).join('')}</div></fieldset><fieldset class="bto-routine-options bto-routine-options--checks"><legend class="bto-routine-srOnly">Routine preferences</legend>${checks.map(([key, label, detail]) => {
        const checked = key === 'sensitive' ? state.answers.sensitive : preferences[key];
        return `<label class="bto-routine-option${checked ? ' is-selected' : ''}"><input type="checkbox" data-preference="${key}"${checked ? ' checked' : ''}><span class="bto-routine-check" aria-hidden="true">✓</span><span><strong>${escapeHTML(label)}</strong><small>${escapeHTML(detail)}</small></span></label>`;
      }).join('')}</fieldset>`;
    };

    const introMarkup = () => `<div class="bto-routine-introCard">
      <div class="bto-routine-introCopy">
        <p class="bto-routine-eyebrow">60-second personalized carrier oil finder</p>
        <h1>Find your <em>oil routine.</em></h1>
        <p class="bto-routine-lead">Answer a few quick questions about where you will use your carrier oil, the texture you prefer, and what matters most. We will show one clear match, helpful alternatives, and a simple way to begin.</p>
        <button class="bto-routine-button bto-routine-button--primary" type="button" data-action="start">Build my routine <span class="bto-routine-arrow" aria-hidden="true"></span></button>
        <div class="bto-routine-introProof" aria-label="Oil finder qualities"><span><b>01</b> No email gate</span><span><b>02</b> About 60 seconds</span><span><b>03</b> Change any answer</span></div>
      </div>
      <aside class="bto-routine-introAside" aria-label="What you will receive">
        <span class="bto-routine-orbit" aria-hidden="true"><i></i><i></i><i></i></span>
        <p class="bto-routine-eyebrow">What you get</p>
        <ol><li><b>Your best fit</b><span>Chosen from currently available Bella Terra oils.</span></li><li><b>A practical ritual</b><span>Where, when, and how much to start with.</span></li><li><b>A meaningful alternative</b><span>A lighter or richer path, not three equal answers.</span></li></ol>
        <small>Find a routine that suits your preferences. Patch test before first use.</small>
      </aside>
    </div>`;

    const questionMarkup = () => {
      const step = currentStep();
      const body = step.key === 'goals' ? goalsMarkup(step) : step.key === 'preferences' ? preferencesMarkup() : optionMarkup(step, step.options());
      return `<div class="bto-routine-shell">
        <aside class="bto-routine-progressPanel">
          <a href="/" class="bto-routine-miniBrand" aria-label="Bella Terra Oils home"><span>Bella Terra</span><small>Oils</small></a>
          <div><p class="bto-routine-eyebrow">Your oil profile</p><strong>${state.step + 1} <i>/</i> ${steps.length}</strong><div class="bto-routine-progressTrack" role="progressbar" aria-label="Step ${state.step + 1} of ${steps.length}" aria-valuemin="1" aria-valuemax="${steps.length}" aria-valuenow="${state.step + 1}"><span style="width:${((state.step + 1) / steps.length) * 100}%"></span></div></div>
          <dl><div><dt>Use</dt><dd>${escapeHTML(LABELS.areas[state.answers.useArea] || 'Not chosen')}</dd></div><div><dt>Priority</dt><dd>${escapeHTML(state.answers.goals[0] ? goalLabel(state.answers.goals[0]) : 'Not chosen')}</dd></div><div><dt>Feel</dt><dd>${escapeHTML(LABELS.finish[String(state.answers.finishChoice)] || 'Not chosen')}</dd></div></dl>
          <button type="button" data-action="restart">Start over</button>
        </aside>
        <section class="bto-routine-question" aria-labelledby="bto-routine-question-title">
          <p class="bto-routine-eyebrow">${escapeHTML(step.eyebrow)}</p>
          <h2 id="bto-routine-question-title" tabindex="-1">${escapeHTML(step.title)}</h2>
          <p class="bto-routine-questionNote">${escapeHTML(step.note)}</p>
          ${body}
          <div class="bto-routine-navigation"><button class="bto-routine-back" type="button" data-action="back">${state.step === 0 ? 'Back to intro' : 'Back'}</button><button class="bto-routine-button bto-routine-button--primary" type="button" data-action="continue"${isComplete() ? '' : ' disabled'}>${state.step === steps.length - 1 ? 'See my routine' : 'Continue'} <span class="bto-routine-arrow" aria-hidden="true"></span></button></div>
        </section>
      </div>`;
    };

    const rank = () => Core.rankProducts(state.answers, profiles, liveProducts, LOGIC_VERSION, { weights: GOAL_WEIGHTS, minimumUseAreaScore: 50, minimumAlternativeScore: 54, tieThreshold: 4, excludeUnknownNutStatus: true });

    const tieMarkup = () => {
      lastResult = rank();
      let tie = Core.chooseTieBreaker(lastResult.primary, lastResult.ranked[1]);
      if ((tie?.kind === 'finish' && state.answers.finish !== null) || (tie?.kind === 'aroma' && state.answers.aroma !== null)) {
        tie = { kind: 'approach', question: 'One last preference: which starting point feels more like you?', options: [{ value: 'simple', label: 'The simplest versatile choice' }, { value: 'specialized', label: 'A more specialized oil' }] };
      }
      if (!tie) { state.phase = 'results'; save(); return resultMarkup(); }
      return `<div class="bto-routine-tie"><p class="bto-routine-eyebrow">One thoughtful tie-breaker</p><h2 tabindex="-1">${escapeHTML(tie.question)}</h2><p>Two oils fit closely. This preference helps us choose between them without pretending the answer is more precise than it is.</p><div class="bto-routine-tieOptions">${tie.options.map((option) => `<button type="button" data-tie-kind="${escapeHTML(tie.kind)}" data-tie-value="${escapeHTML(option.value)}"><strong>${escapeHTML(option.label)}</strong><span class="bto-routine-arrow" aria-hidden="true"></span></button>`).join('')}</div><button class="bto-routine-back" type="button" data-action="back-from-tie">Back to answers</button></div>`;
    };

    const productImage = (item) => item.liveProduct.customImage || window.BTORoutineImages?.[item.profile.handle] || '';
    const loadProductImage = (item) => {
      const handle = item?.profile?.handle;
      const loader = item?.liveProduct?.imageLoader;
      if (!handle || !loader || productImage(item)) return Promise.resolve(productImage(item));
      if (imagePromises.has(handle)) return imagePromises.get(handle);
      const promise = new Promise((resolve) => {
        const script = document.createElement('script');
        script.src = loader;
        script.async = true;
        script.onload = () => resolve(productImage(item));
        script.onerror = () => resolve('');
        document.head.appendChild(script);
      });
      imagePromises.set(handle, promise);
      return promise;
    };
    const hydrateResultImages = () => {
      resultImageObserver?.disconnect();
      resultImageObserver = null;
      const hydrateCard = (card) => {
        const handle = card.dataset.productCard;
        const item = lastResult?.ranked?.find((candidate) => candidate.profile.handle === handle);
        const image = card.querySelector('[data-product-image]');
        if (!item || !image || image.getAttribute('src')) return;
        loadProductImage(item).then((source) => {
          if (!source || !image.isConnected) return;
          image.src = source;
        });
      };
      const cards = [...stage.querySelectorAll('[data-product-card]')];
      const primary = cards.find((card) => !card.classList.contains('bto-routine-product--compact'));
      if (primary) hydrateCard(primary);
      const deferred = cards.filter((card) => card !== primary);
      if (!deferred.length) return;
      if (!('IntersectionObserver' in window)) {
        deferred.forEach(hydrateCard);
        return;
      }
      resultImageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          hydrateCard(entry.target);
          observer.unobserve(entry.target);
        });
      }, { rootMargin: '280px 0px' });
      deferred.forEach((card) => resultImageObserver.observe(card));
    };
    const activeVariant = (item) => Core.selectVariant(item.liveProduct);

    const answerReasons = (item) => {
      const area = LABELS.areas[state.answers.useArea]?.toLowerCase() || 'routine';
      const goal = state.answers.goals[0] ? goalLabel(state.answers.goals[0]).toLowerCase() : 'top priority';
      const finish = item.profile.finish <= 2 ? 'lighter' : item.profile.finish >= 4 ? 'richer' : 'balanced';
      return [
        `It scores strongly for your ${area} routine and ${goal}.`,
        `Its ${item.profile.copy.texture} feel matches a ${finish} starting point.`,
        item.profile.copy.why?.[2] || 'It offers a clear, practical place to begin.'
      ];
    };

    const productCardMarkup = (item, role, compact = false) => {
      if (!item) return '';
      const variant = activeVariant(item);
      if (!variant) return '';
      const why = answerReasons(item);
      const image = productImage(item);
      const profile = item.profile;
      const nutrient = Core.nutrientProfile(profile.handle);
      return `<article class="bto-routine-product${compact ? ' bto-routine-product--compact' : ''}" data-product-card="${escapeHTML(profile.handle)}">
        <div class="bto-routine-productMedia"><img${image ? ` src="${escapeHTML(image)}"` : ''} data-product-image alt="${escapeHTML(item.liveProduct.title || profile.name)} bottle" width="800" height="800" loading="${compact ? 'lazy' : 'eager'}" decoding="async"><span class="bto-routine-imageFallback" aria-hidden="true">BT</span><small>${escapeHTML(role)}</small></div>
        <div class="bto-routine-productCopy">
          <p class="bto-routine-eyebrow">${escapeHTML(role)}</p><h3>${escapeHTML(item.liveProduct.title || profile.name)}</h3>
          ${matchBars(compact ? 4 : 5, compact ? 'Recommended pairing' : 'Your first choice')}
          <div class="bto-routine-productFacts"><span><strong>Texture:</strong> ${escapeHTML(profile.copy.texture)}</span><span><strong>Aroma:</strong> ${escapeHTML(profile.copy.aromaDescription)}</span></div>
          ${nutrient ? `<div class="bto-routine-nutrients"><strong>${escapeHTML(nutrient.title)}</strong><p>${escapeHTML(nutrient.text)}</p></div>` : ''}
          ${compact ? `<p>${escapeHTML(profile.routines[state.answers.useArea])}</p>` : `<ul>${why.map((reason) => `<li>${escapeHTML(reason)}</li>`).join('')}</ul><div class="bto-routine-ritual"><small>Your starting ritual</small><p>${escapeHTML(profile.routines[state.answers.useArea])}</p></div>`}
          <div class="bto-routine-buyRow">
            <label><span>Available size</span><select data-variant-select>${(item.liveProduct.variants || []).filter((option) => option.available).map((option) => `<option value="${escapeHTML(option.id)}" data-price="${escapeHTML(option.price)}"${String(option.id) === String(variant.id) ? ' selected' : ''}>${escapeHTML(option.title)} · ${formatMoney(option.price)}</option>`).join('')}</select></label>
            <button class="bto-routine-button bto-routine-button--buy" type="button" data-add-product="${escapeHTML(profile.handle)}" data-variant-id="${escapeHTML(variant.id)}"><span>${compact ? 'Add my companion' : 'Add my best match'}</span><b data-live-price>${formatMoney(variant.price)}</b></button>
          </div>
          <p class="bto-routine-cartStatus" data-cart-status role="status"></p>
          <a class="bto-routine-productLink" href="${escapeHTML(item.liveProduct.url || profile.canonicalUrl)}" data-product-link="${escapeHTML(profile.handle)}">See product details <span class="bto-routine-arrow" aria-hidden="true"></span></a>
        </div>
      </article>`;
    };

    const matchBars = (filled, label) => `<div class="bto-routine-match"><span>Quiz match · ${escapeHTML(label)}</span><span class="bto-routine-matchBars" role="img" aria-label="${escapeHTML(label)}: ${filled} of 5 quiz match bars">${Array.from({length:5}, (_, index) => `<i class="${index < filled ? 'is-filled' : ''}" aria-hidden="true"></i>`).join('')}</span></div>`;

    const secondaryFor = (result) => Core.selectCompanion(result, state.answers);

    const resultMarkup = () => {
      lastResult = rank();
      const primary = lastResult.primary;
      if (!primary) return `<div class="bto-routine-empty"><p class="bto-routine-eyebrow">Let us help directly</p><h2 tabindex="-1">Your preferences removed every current match.</h2><p>That is useful information—not a reason to force a recommendation. Edit your preferences or contact Bella Terra for a careful starting point.</p><div><button class="bto-routine-button bto-routine-button--primary" data-action="edit">Edit answers</button><a href="/pages/contact-us">Contact us</a></div></div>`;
      const secondary = secondaryFor(lastResult);
      const pairing = secondary ? Core.pairingDetails(primary, secondary, state.answers) : null;
      const chips = [LABELS.areas[state.answers.useArea], state.answers.goals[0] ? goalLabel(state.answers.goals[0]) : null, LABELS.finish[String(state.answers.finishChoice)], LABELS.complexity[state.answers.complexity]].filter(Boolean);
      const alternatives = lastResult.alternatives.filter((item) => item.profile.handle !== secondary?.profile.handle).slice(0, 2);
      const headline = state.answers.complexity === 'one' ? 'Your easiest one-oil routine.' : 'Your easiest oil routine.';
      return `<div class="bto-routine-results">
        <header class="bto-routine-resultsHeader"><div><p class="bto-routine-eyebrow">Built for your answers</p><h2 tabindex="-1">${escapeHTML(headline)}</h2><p>${secondary ? 'Your best match, plus a complementary oil chosen to bring more to your routine.' : 'Your best match, with a simple ritual shaped around your priorities.'}</p></div><div class="bto-routine-answerChips">${chips.map((chip) => `<span>${escapeHTML(chip)}</span>`).join('')}</div></header>
        ${productCardMarkup(primary, 'Your best match')}
        ${secondary ? `<section class="bto-routine-secondary"><div><p class="bto-routine-eyebrow">Better together</p><h2>${escapeHTML(pairing.title)}</h2><p class="bto-routine-pairGoal">${escapeHTML(pairing.goal)}</p><p>${escapeHTML(pairing.benefit)}</p><div class="bto-routine-pairRitual"><strong>Your two-oil ritual</strong><p>${escapeHTML(pairing.ritual)}</p></div></div>${productCardMarkup(secondary, 'Your recommended companion', true)}</section>` : ''}
        ${alternatives.length ? `<section class="bto-routine-alternatives"><div><p class="bto-routine-eyebrow">Other good paths</p><h2>Prefer a different feel?</h2></div><div>${alternatives.map((item) => `<a href="${escapeHTML(item.liveProduct.url || item.profile.canonicalUrl)}" data-product-link="${escapeHTML(item.profile.handle)}"><span><small>${escapeHTML(item.distinction)}</small><strong>${escapeHTML(item.liveProduct.title || item.profile.name)}</strong>${matchBars(4, 'Recommended alternative')}</span><span class="bto-routine-arrow" aria-hidden="true"></span></a>`).join('')}</div></section>` : ''}
        <aside class="bto-routine-safety"><span aria-hidden="true">+</span><div><strong>Start low. Patch test first.</strong><p>Try a small amount on a limited area before broader use. Stop if irritation occurs. This finder offers cosmetic routine guidance; it does not diagnose or treat a condition.</p></div></aside>
        <details class="bto-routine-method"><summary>How your match was chosen <span aria-hidden="true">+</span></summary><div><p>We first removed unavailable products and anything that conflicted with your ingredient preferences. Then we weighted your use area, ranked priorities, personal context, preferred finish, and sensitivity preference. Five match bars mark your first choice; four mark a recommended companion or alternative. The bars show this quiz’s recommendation order; texture is described separately.</p><p>Current product title, availability, sizes, price, and link come directly from Shopify. Recommendation copy comes from Bella Terra's approved cosmetic product profiles.</p></div></details>
        <div class="bto-routine-resultActions"><button class="bto-routine-button bto-routine-button--primary" type="button" data-action="edit">Edit my answers</button><button class="bto-routine-back" type="button" data-action="restart">Start over</button></div>
      </div>`;
    };

    // Anchor the new reading position, rather than the top of the quiz container.
    // Selecting an answer uses render(false); only navigation moves the viewport.
    let stepPositionVersion = 0;
    const resetStepPosition = () => {
      const version = ++stepPositionVersion;
      const heading = stage.querySelector('h2[tabindex="-1"], h1');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.focus({ preventScroll: true });
      const align = () => {
        if (version !== stepPositionVersion || !heading.isConnected) return;
        const header = document.querySelector('.bto-global-header');
        const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
        const top = window.scrollY + heading.getBoundingClientRect().top - headerBottom - 20;
        window.scrollTo({ top: Math.max(0, top), behavior: 'instant' });
      };
      window.requestAnimationFrame(() => {
        align();
        window.requestAnimationFrame(align);
      });
    };

    const render = (focus = true) => {
      if (state.phase === 'intro') stage.innerHTML = introMarkup();
      if (state.phase === 'questions') stage.innerHTML = questionMarkup();
      if (state.phase === 'tie') stage.innerHTML = tieMarkup();
      if (state.phase === 'results') stage.innerHTML = resultMarkup();
      app.dataset.phase = state.phase;
      save();
      if (state.phase === 'results') hydrateResultImages();
      if (focus) resetStepPosition();
    };

    const updateOptionClasses = (input) => {
      input.closest('.bto-routine-option')?.classList.toggle('is-selected', input.checked);
      if (input.type === 'radio') input.closest('fieldset')?.querySelectorAll('.bto-routine-option').forEach((label) => label.classList.toggle('is-selected', label.contains(input)));
    };

    stage.addEventListener('change', (event) => {
      const input = event.target;
      if (input.matches('[data-answer]')) {
        const key = input.dataset.answer;
        let value = input.value;
        state.answers.tieBreaker = null;
        if (key === 'finish') {
          state.answers.finishChoice = value;
          state.answers.finish = value === 'none' ? null : Number(value);
        } else {
          state.answers[key] = value;
          if (key === 'useArea') {
            state.answers.profile = null;
            state.answers.goals = [];
          }
          if (key === 'profile' && value === 'sensitive') state.answers.sensitive = true;
        }
        updateOptionClasses(input);
        render(false);
        refocus(`[data-answer="${key}"][value="${value}"]`);
      }
      if (input.matches('[data-goal]')) {
        const goal = input.value;
        if (input.checked && !state.answers.goals.includes(goal)) {
          if (state.answers.goals.length >= 3) { input.checked = false; announce('Choose up to three priorities. Remove one before adding another.'); return; }
          state.answers.goals.push(goal);
        } else if (!input.checked) state.answers.goals = state.answers.goals.filter((item) => item !== goal);
        state.answers.tieBreaker = null;
        render(false);
        refocus(`[data-goal][value="${goal}"]`);
      }
      if (input.matches('[data-preference]')) {
        const key = input.dataset.preference;
        if (key === 'sensitive') state.answers.sensitive = input.checked;
        else state.answers.preferences[key] = input.checked;
        state.answers.tieBreaker = null;
        updateOptionClasses(input);
        save();
      }
      if (input.matches('[data-aroma]')) {
        state.answers.aromaChoice = input.value;
        state.answers.aroma = input.value === 'none' ? null : Number(input.value);
        state.answers.tieBreaker = null;
        render(false);
        refocus(`[data-aroma][value="${input.value}"]`);
      }
      if (input.matches('[data-variant-select]')) {
        const card = input.closest('[data-product-card]');
        const button = card?.querySelector('[data-add-product]');
        const option = input.selectedOptions[0];
        if (button && option) {
          button.dataset.variantId = input.value;
          button.querySelector('[data-live-price]').textContent = formatMoney(option.dataset.price);
        }
      }
    });

    stage.addEventListener('click', async (event) => {
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (action === 'start') {
        state.phase = 'questions'; state.step = 0; state.restored = false; completedSteps.clear(); pushEvent('started'); render(); return;
      }
      if (action === 'continue') {
        if (!isComplete()) return;
        if (!completedSteps.has(currentStep().key)) {
          pushEvent('step_completed', { finder_step_number: state.step + 1, finder_step_key: currentStep().key });
          completedSteps.add(currentStep().key);
        }
        if (state.step < steps.length - 1) state.step += 1;
        else {
          const result = rank();
          state.phase = result.needsTieBreaker ? 'tie' : 'results';
          if (state.phase === 'results') pushEvent('result_viewed', { finder_result_count: result.ranked.length, finder_primary_handle: result.primary?.profile.handle });
        }
        render(); return;
      }
      if (action === 'back') {
        if (state.step > 0) state.step -= 1;
        else state.phase = 'intro';
        render(); return;
      }
      if (action === 'back-from-tie') { state.phase = 'questions'; state.step = steps.length - 1; render(); return; }
      if (action === 'edit') { state.answers.tieBreaker = null; state.phase = 'questions'; state.step = 0; render(); return; }
      if (action === 'restart') {
        try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
        state = defaultState(); lastResult = null; completedSteps.clear(); pushEvent('retaken'); render(); return;
      }

      const rankUp = event.target.closest('[data-rank-up]')?.dataset.rankUp;
      const rankDown = event.target.closest('[data-rank-down]')?.dataset.rankDown;
      if (rankUp || rankDown) {
        const goal = rankUp || rankDown;
        const from = state.answers.goals.indexOf(goal);
        const to = rankUp ? from - 1 : from + 1;
        if (from >= 0 && to >= 0 && to < state.answers.goals.length) {
          [state.answers.goals[from], state.answers.goals[to]] = [state.answers.goals[to], state.answers.goals[from]];
          state.answers.tieBreaker = null;
          announce(`${goalLabel(goal)} is now priority ${to + 1}.`); render(false);
          refocus(`[data-goal][value="${goal}"]`);
        }
        return;
      }

      const tieButton = event.target.closest('[data-tie-value]');
      if (tieButton) {
        state.answers.tieBreaker = { kind: tieButton.dataset.tieKind, value: tieButton.dataset.tieValue };
        state.phase = 'results';
        const result = rank();
        pushEvent('result_viewed', { finder_result_count: result.ranked.length, finder_primary_handle: result.primary?.profile.handle, finder_tie_breaker_used: true });
        render(); return;
      }

      const productLink = event.target.closest('[data-product-link]');
      if (productLink) pushEvent('product_details_clicked', { finder_product_handle: productLink.dataset.productLink });

      const addButton = event.target.closest('[data-add-product]');
      if (addButton) {
        const card = addButton.closest('[data-product-card]');
        const status = card.querySelector('[data-cart-status]');
        const variantId = addButton.dataset.variantId;
        if (!variantId || addButton.disabled) return;
        addButton.disabled = true;
        status.textContent = 'Adding your selected size…';
        try {
          const rootPath = window.Shopify?.routes?.root || '/';
          const response = await fetch(`${rootPath}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }) });
          if (!response.ok) throw new Error('Cart request failed');
          const cartUrl = `${String(rootPath).replace(/\/?$/, '/')}cart`;
          status.innerHTML = `Added. <a href="${escapeHTML(cartUrl)}">View cart</a>`;
          addButton.querySelector('span').textContent = 'Added';
          const selectedOption = card.querySelector('[data-variant-select]')?.selectedOptions?.[0];
          pushEvent('add_to_cart_succeeded', { finder_product_handle: addButton.dataset.addProduct, finder_variant_id: String(variantId), finder_price_cents: Number(selectedOption?.dataset.price || 0), finder_currency: currency });
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        } catch (_) {
          status.textContent = 'We could not add that size. Please open the product page and try again.';
          addButton.disabled = false;
          pushEvent('add_to_cart_failed', { finder_product_handle: addButton.dataset.addProduct, finder_variant_id: String(variantId) });
        }
      }
    });

    document.addEventListener('visitorConsentCollected', () => {
      if (privacyAllowed('preferences')) save();
      else {
        try { window.sessionStorage.removeItem(STORAGE_KEY); } catch (_) {}
      }
    });
    if (state.restored && state.phase !== 'intro') announce('Your answers from this browser session were restored.');
    render(false);
  }

  const legacyOverlaySelector = [
    
    '#voice-bot-wrapper', '#voice-bot-panel', '#codex-tidio-close',
    '.codex-widget-close', '#zooomysalespop', 'shopify-forms-embed'
  ].join(',');
  const removeLegacyOverlays = (scope = document) => {
    if (scope.nodeType === 1 && scope.matches?.(legacyOverlaySelector)) scope.remove();
    scope.querySelectorAll?.(legacyOverlaySelector).forEach((element) => element.remove());
    scope.querySelectorAll?.('button').forEach((button) => {
      if (button.textContent.trim() !== 'Get 10% Off' || button.closest('[data-bt-welcome-signup]')) return;
      (button.closest('[class*="_container_"]') || button.parentElement || button).remove();
    });
  };
  removeLegacyOverlays();
  if (!window.btoRoutineOverlayObserver && document.body) {
    window.btoRoutineOverlayObserver = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === 1) removeLegacyOverlays(node);
    })));
    window.btoRoutineOverlayObserver.observe(document.body, { childList: true, subtree: true });
  }
  const isPreview = /(?:[?&]view=bto-routine-builder|[?&]preview_theme_id=)/.test(window.location.search) || Boolean(window.Shopify?.designMode);
  if (isPreview && !document.head.querySelector('meta[data-bto-preview-robots]')) {
    const robots = document.createElement('meta'); robots.name = 'robots'; robots.content = 'noindex,nofollow'; robots.dataset.btoPreviewRobots = 'true'; document.head.appendChild(robots);
  }

  const boot = (scope = document) => scope.querySelectorAll(SELECTOR).forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
})();
